import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import { formatDate, formatDateTime } from '../../../../app/utils'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../../../app/hooks'
import {
  useGetCurrenciesByCustomerIdQuery,
  useGetShipmentByIdQuery,
} from '../../../../app/redux-fetch/apiOrders'
import { TableColumn, TableItem, TableItemTypography } from '../../../../components/Headers/styles'
import { fetchLoggedInUserSelector } from '../../../selectors'
import { ILoggedInUser } from '../../../types'
import { timelineMessages } from './messages'

const Timeline = () => {
  const { id } = useParams()
  const loggedInUser = useAppSelector(fetchLoggedInUserSelector.data) || ({} as ILoggedInUser)
  const tenantId = loggedInUser?.tenantId
  const customerId = loggedInUser?.customerId
  const isTenantUser = tenantId && !customerId

  const renderMoneyValue = (value: number, currencyId: number) => {
    return `${currencies.find((c) => c.id === currencyId)?.iso3} ${value
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`
  }

  const { data: order } = useGetShipmentByIdQuery({
    shipmentId: Number(id),
    tenantId,
  })

  const { data: currencies = [] } = useGetCurrenciesByCustomerIdQuery(
    {
      customerId: order?.customerId ?? 0,
      hideDisabled: true,
    },
    { skip: !order }
  )

  const orderStatuses = useMemo(() => order?.statuses || [], [order])
  const bookings = useMemo(() => order?.bookings || [], [order])

  return (
    <Box display='flex' flexDirection='column' sx={{ pt: '32px' }}>
      {orderStatuses.length > 0 && (
        <>
          <Box display='flex'>
            <TableColumn>
              <TableItemTypography variant='body1'>
                <FormattedMessage {...timelineMessages.date} />
              </TableItemTypography>
            </TableColumn>
            <TableColumn>
              <TableItemTypography variant='body1'>
                <FormattedMessage {...timelineMessages.status} />
              </TableItemTypography>
            </TableColumn>
            <TableColumn>
              <TableItemTypography variant='body1'>
                <FormattedMessage {...timelineMessages.updatedBy} />
              </TableItemTypography>
            </TableColumn>
          </Box>
          {orderStatuses.map((status: any, idx: any) => (
            <Box key={`status-${idx}`} display='flex'>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{formatDateTime(status.statusDate)}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{status.orderStatusType.name}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{`${status.updatedByUser?.firstName} ${status.updatedByUser?.lastName}`}</Typography>
              </TableItem>
            </Box>
          ))}
          <Box sx={{ my: 3, borderBottom: '1px solid #e0e0e0' }} />
        </>
      )}

      <Box display='flex'>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.load} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.quantity} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.description} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.pickupLocation} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.consignee} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.requestedPickupDate} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1'>
            <FormattedMessage {...timelineMessages.requestedDeliveryDate} />
          </TableItemTypography>
        </TableColumn>
        <TableColumn>
          <TableItemTypography variant='body1' sx={{ textAlign: 'center' }}>
            <FormattedMessage {...timelineMessages.value} />
          </TableItemTypography>
        </TableColumn>
      </Box>
      {bookings.map((load: any, idx: any) => {
        const routeLoadHistories = isTenantUser
          ? load.routeLoadHistories
          : load.routeLoadHistories?.filter((history: any) => history.completedDate)
        const deliveryLocation = load.waypoints.find((x: any) => x.waypointTypeId === 3).location
        const pickupLocation = load.waypoints.find((x: any) => x.waypointTypeId === 1).location
        return (
          <>
            <Box key={idx} display='flex'>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography sx={{ fontWeight: 700 }}>Load #{load?.displayOrder}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{load.quantity}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{load.description}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{pickupLocation.name}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>{deliveryLocation.name}</Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>
                  {formatDate(load.transportDetail?.pickupDate)}
                </Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0}>
                <Typography>
                  {formatDate(load.transportDetail?.deliveryDate)}
                </Typography>
              </TableItem>
              <TableItem isOdd={idx % 2 === 0} sx={{ textAlign: 'right' }}>
                <Typography>{renderMoneyValue(load.value ?? 0, load.currencyId ?? -1)}</Typography>
              </TableItem>
            </Box>
            {routeLoadHistories && routeLoadHistories.length > 0 && (
              <Box display='flex' sx={{ pl: '36px' }}>
                {isTenantUser && (
                  <TableColumn>
                    <TableItemTypography variant='body1'>
                      <FormattedMessage {...timelineMessages.scheduledDate} />
                    </TableItemTypography>
                  </TableColumn>
                )}
                <TableColumn>
                  <TableItemTypography variant='body1'>
                    <FormattedMessage {...timelineMessages.completedDate} />
                  </TableItemTypography>
                </TableColumn>
                <TableColumn>
                  <TableItemTypography variant='body1'>
                    <FormattedMessage {...timelineMessages.location} />
                  </TableItemTypography>
                </TableColumn>
                <TableColumn>
                  <TableItemTypography variant='body1'>
                    <FormattedMessage {...timelineMessages.action} />
                  </TableItemTypography>
                </TableColumn>
              </Box>
            )}

            {routeLoadHistories &&
              routeLoadHistories.map((history: any, aIdx: any) => {
                const isColoredBackground = aIdx % 2 === 0
                return (
                  <Box key={aIdx} display='flex' sx={{ pl: '36px' }}>
                    {isTenantUser && (
                      <TableItem isOdd={isColoredBackground}>
                        <Typography sx={{ fontWeight: 700 }}>
                          {formatDateTime(history.scheduledDate)}
                        </Typography>
                      </TableItem>
                    )}
                    <TableItem isOdd={isColoredBackground}>
                      <Typography sx={{ ...(!isTenantUser && { fontWeight: 700 }) }}>
                                                {history.completedDate
                                                  ? formatDateTime(history.completedDate)
                                                  : 'Scheduled'}
                      </Typography>
                    </TableItem>
                    <TableItem isOdd={isColoredBackground}>
                      <Typography>{history.location}</Typography>
                    </TableItem>
                    <TableItem isOdd={isColoredBackground}>
                      <Typography>{history.action}</Typography>
                    </TableItem>
                  </Box>
                )
              })}
          </>
        )
      })}
    </Box>
  )
}

export default Timeline
